import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from "pure-react-carousel";
import { ReactComponent as LeftArrow } from "../../../../assets/left-arrow-calendar.svg";
import { ReactComponent as RigthArrow } from "../../../../assets/rigth-arrow-calendar.svg";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import {
  getContentById,
  getContentByProperty,
} from "../../../../services/content.service";
import { ContentType } from "../../../../enums";
import {
  ExperienceContent,
  IContent,
  WebinarContent,
} from "../../../../@types";
import { useNavigate } from "react-router-dom";
import useWindowSize from "../../../../hooks/useWindowSize";
import { useContext, useEffect, useState } from "react";
import { formatDateRange } from "../../../../utils";
import { AuthContext } from "../../../../contexts/AuthContext";
import Button from "../../../../components/button";
import { ButtonVariant } from "../../../../components/button/types";
import {
  addToWaitingList,
  cancelWebinar,
  isUserOnWaitingList,
  registerWebinar,
  removeFromWaitingList,
} from "../../../../services/webinar.service";
import { CREDITS_ERRORS } from "../../../../errors/errorCodes";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const Story = ({ content }: { content: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("community");

  const { user } = useContext(AuthContext);

  const {
    data: webinar,
    isLoading,
    refetch: refetchWebinar,
  } = useQuery(["webinar", content], () =>
    getContentById({
      platformContentId: content.platformContentId,
      projection: "min",
    })
  );
  const getUserTimeZone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [canRegister, setCanRegister] = useState(false);
  const [canCancel, setCanCancel] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [buttonText, setButtonText] = useState<string | null>("");

  const [waitListButtonloading, setWaitListButtonloading] = useState(false);

  const [maxParticipantsReached, setMaxParticipantsReached] = useState(false);
  const [canBeAddedToWaitlist, setCanBeAddedToWaitlist] = useState(false);
  const [waitingListButtonText, setwaitingListButtonText] = useState<
    string | null
  >("");

  const { data: isOnWaitList, refetch: refetchWaitList } = useQuery(
    ["waitList", content.platformContentId],
    () => isUserOnWaitingList(content.platformContentId)
  );

  useEffect(() => {
    if (!isLoading) {
      setCanBeAddedToWaitlist(!isOnWaitList);
      setwaitingListButtonText(
        !isOnWaitList
          ? t('add-to-waitList-button-text')
          : t('remove-from-waitList-button-text')
      );
    }
  }, [isOnWaitList, isLoading]);

  const registerMutation = useMutation(
    (mutationArgs: {
      webinarId: string;
      webinarCost: number;
      userTimeZone: string;
    }) =>
      registerWebinar({
        webinarId: mutationArgs.webinarId,
        webinarCost: mutationArgs.webinarCost,
        userTimeZone: mutationArgs.userTimeZone,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage(t("webinar-success"));
        refetchWebinar();
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.INSUFFICIENT_CREDITS) {
          setRegisterError(t("errors.insufficient-credits"));
        } else if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setRegisterError(t("errors.not-found-webinar"));
        } else {
          console.error("register webinar error:", response);
        }
      },
    }
  );

  const cancelMutation = useMutation(
    ["cancel"],
    (mutationArgs: { webinarId: string; userTimeZone: string }) =>
      cancelWebinar({
        webinarId: mutationArgs.webinarId,
        userTimeZone: mutationArgs.userTimeZone,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage("");
        refetchWebinar();
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setCancelError(t("errors.not-found-webinar"));
        } else {
          console.error("register webinar error:", response);
        }
      },
    }
  );

  const addToWaitListMutation = useMutation(
    ["addToWaitList"],
    (mutationArgs: { contentId: string }) =>
      addToWaitingList({
        contentId: mutationArgs.contentId,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage(t("added-to-waitList-success"));
        setwaitingListButtonText(t("add-to-waitList-button-text"));
        refetchWebinar();
        refetchWaitList();
        setWaitListButtonloading(false);
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setCancelError(t("errors.not-found-webinar"));
        } else {
          console.error("add to waitlist webinar error:", response);
        }
      },
    }
  );

  const removeFromWaitListMutation = useMutation(
    ["removeFromWaitList"],
    (mutationArgs: { contentId: string }) =>
      removeFromWaitingList({
        contentId: mutationArgs.contentId,
      }),
    {
      onSuccess: async () => {
        setSuccessMessage(t("removed-from-waitList-success"));
        setwaitingListButtonText(t("remove-from-waitList-button-text"));
        refetchWebinar();
        refetchWaitList();
        setWaitListButtonloading(false);
      },
      onError: ({ response }) => {
        if (response.data.message == CREDITS_ERRORS.WEBINAR_NOT_FOUND) {
          setCancelError(t("errors.not-found-webinar"));
        } else {
          console.error("removed from waitlist webinar error:", response);
        }
      },
    }
  );

  const handleRegisterClick = (webinar: any) => {
    const webinarId = String(webinar.id);
    const webinarCost = Number(webinar.credits);
    const userTimeZone = getUserTimeZone();
    if (!canRegister) return;
    setCanRegister(false);
    setCanCancel(false);
    registerMutation.mutate({
      webinarId: webinarId,
      webinarCost: webinarCost,
      userTimeZone: userTimeZone,
    });
  };

  const handleCancelClick = (webinar: any) => {
    const webinarId = String(webinar.id);
    const userTimeZone = getUserTimeZone();

    if (!canCancel) return;
    setCanRegister(false);
    setCanCancel(false);
    cancelMutation.mutate({
      webinarId: webinarId,
      userTimeZone: userTimeZone,
    });
  };

  const handleWaitListClick = async () => {
    if (waitListButtonloading) return;
    setWaitListButtonloading(true);

    if (canBeAddedToWaitlist) {
      addToWaitListMutation.mutate({contentId: content.platformContentId});
    } else {
      removeFromWaitListMutation.mutate({contentId: content.platformContentId});
    }
  };

  useEffect(() => {
    if (
      webinar &&
      user?.availableCredits !== null &&
      user?.availableCredits !== undefined &&
      content.participants !== undefined
    ) {
      const now = new Date();
      const webinarStartDate = new Date((webinar as WebinarContent).startDate);
      const webinarEndDate = new Date((webinar as WebinarContent).endDate);
      const oneHourBefore = new Date(
        webinarStartDate.getTime() - 60 * 60 * 1000
      );

      const twentyFourHoursBefore = new Date(
        webinarStartDate.getTime() - 24 * 60 * 60 * 1000
      );

      const hasEnoughCredits =
        user?.availableCredits >= (webinar as WebinarContent).credits;
      const isInFuture = now < oneHourBefore;
      const maxParticipantsNotReached =
        content.participants < (webinar as WebinarContent).maxParticipants;

      if (maxParticipantsNotReached) {
        setMaxParticipantsReached(false);
      } else {
        setMaxParticipantsReached(true);
      }
      const isMoreThan24HoursBefore = now < twentyFourHoursBefore;
      const isRegistered = (webinar as WebinarContent).registered;
      const hasEnded = now > webinarEndDate;

      if (hasEnded) {
        setHasEnded(true);
        setCanRegister(false);
      } else if (
        content.participants >= (webinar as WebinarContent).maxParticipants
      ) {
        setButtonText(t("webinar-registration-max"));
        setCanRegister(false);
      } else if (!hasEnoughCredits && canRegister) {
        setButtonText(t("webinar-not-available-credits"));
        setCanRegister(false);
      } else if (!isInFuture && canRegister) {
        setButtonText(t("webinar-registration-closed"));
        setCanRegister(false);
        // } else if (!isMoreThan24HoursBefore) {
        //   setButtonText(t("webinar-under-24-hours"));
        //   setCanRegister(false);
        // }
      } else {
        if ((webinar as WebinarContent).credits === 0) {
          setButtonText(
            t("webinar-register-zero", {
              placeholder: (webinar as WebinarContent).credits,
            })
          );
        } else if ((webinar as WebinarContent).credits === 1) {
          setButtonText(
            t("webinar-register-one", {
              placeholder: (webinar as WebinarContent).credits,
            })
          );
        } else if ((webinar as WebinarContent).credits > 1) {
          setButtonText(
            t("webinar-register-other", {
              placeholder: (webinar as WebinarContent).credits,
            })
          );
        }

        setCanRegister(true);
      }
      setCanCancel(isMoreThan24HoursBefore && isRegistered);
      setCanRegister(
        hasEnoughCredits &&
          isInFuture &&
          maxParticipantsNotReached &&
          !isRegistered
      );
    }
  }, [webinar, user?.availableCredits, content.participants]);
  if (isLoading || !webinar) {
    return null;
  }

  return (
    <div
      className="relative  cursor-pointer"
      onClick={() =>
        navigate(`/community/webinars/${content?.platformContentId}`)
      }
    >
      <img
        className="object-cover rounded-t-lg"
        src={`https:${(webinar as WebinarContent)?.image}`}
        alt="about"
      />
      <div className="relative h-[230px] p-2 border border-Grey-light-2 border-t-0 rounded-b-lg overflow-hidden flex flex-col">
        <div className="flex-grow">
          <p className=" font-semibold mb-1">
            {(webinar as WebinarContent)?.title}
          </p>
          <p className="text-md text-Yellow-default font-bold mb-2">
            {((webinar as WebinarContent)?.hosts || [])
              .map((host) => `${host.fields.givenName} ${host.fields.surname}`)
              .join(", ")}
          </p>

          <p className="text-sm font-medium mb-2">
            {formatDateRange(
              (webinar as WebinarContent)?.startDate,
              (webinar as WebinarContent)?.endDate
            )}
          </p>

          {(webinar as WebinarContent).registered && !hasEnded && (
            <>
              <div>
                <CheckCircleOutlineIcon className="text-[#048273] mb-1 mr-2" />
                <span className="text-sm font-medium text-gray-500">
                  {t("webinar-under-24-hours")}
                </span>
              </div>
            </>
          )}
          {hasEnded && (
            <span className="text-sm font-medium text-gray-500">
              {(webinar as WebinarContent).registered
                ? t("webinar-participated")
                : t("webinar-ended")}
            </span>
          )}
        </div>
        <div className="mt-auto">
          <div className="flex items-center justify-end w-full">
            {!canCancel &&
              !hasEnded &&
              !(webinar as WebinarContent).registered &&
              !maxParticipantsReached && (
                <>
                  <button
                    type="button"
                    className={`btn px-4 py-1.5 w-full  ${
                      canRegister ? "bg-[#FDC300]" : "bg-[#E4E4E4]"
                    }`}
                    style={{
                      backgroundColor: canRegister ? "#FDC300" : "#E4E4E4",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRegisterClick(webinar);
                    }}
                    disabled={!canRegister}
                  >
                    {buttonText}
                  </button>
                </>
              )}
            {maxParticipantsReached && !(webinar as WebinarContent).registered && (
              <>
                <button
                  type="button"
                  className={`btn px-4 py-1.5 w-full border border-black ${
                    canBeAddedToWaitlist ? "bg-[#FFFFFF]" : "bg-[#FFFFFF]"
                  }`}
                  style={{
                    backgroundColor: canBeAddedToWaitlist
                      ? "#FFFFFF"
                      : "#FFFFFF",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleWaitListClick();
                  }}
                  disabled={waitListButtonloading}
                >
                  {waitingListButtonText}
                </button>
              </>
            )}

            {/* {canCancel && (
              <Button
                variant={ButtonVariant.SECONDARY}
                className="bg-[#757575] px-4 py-1.5 w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelClick(webinar);
                }}
              >
                {t("webinar-cancel")}
              </Button>
            )} */}
          </div>
          <div className="mt-2">
            {successMessage && (
              <div className="text-sm text-green-700 ml-2">
                {successMessage}
              </div>
            )}
            {registerError && (
              <div className="text-sm text-red ml-2">{registerError}</div>
            )}
            {cancelError && (
              <div className="text-sm text-red ml-2">{cancelError}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export function Webinars() {
  const { t } = useTranslation("community");

  const [slidesNumber, setSlidesNumber] = useState(3);

  const {
    data: webinars,
    isLoading,
    refetch: refetchWebinars,
  } = useQuery<any[]>(["webinars"], () =>
    getContentByProperty({ contentType: ContentType.WEBINAR })
  );

  const windowSize = useWindowSize();

  useEffect(() => {
    const width = windowSize || 1440;

    if (width >= 1640 && slidesNumber !== 3) {
      setSlidesNumber(3);
    } else if (width < 1640 && width >= 1140 && slidesNumber !== 2) {
      setSlidesNumber(2);
    } else if (width < 1140 && width >= 1024 && slidesNumber !== 1) {
      setSlidesNumber(1);
    } else if (width < 1024 && width >= 900 && slidesNumber !== 3) {
      setSlidesNumber(3);
    } else if (width < 900 && width >= 640 && slidesNumber !== 2) {
      setSlidesNumber(2);
    } else if (width < 640 && slidesNumber !== 1) {
      setSlidesNumber(1);
    }
  }, [windowSize]);

  if (isLoading || !webinars || webinars?.length === 0) {
    return null;
  }

  const showButtons = webinars.length > slidesNumber;

  return (
    <>
      <p className="text-lg text-Grey-dark font-bold mb-6 ">
        {t("restart-webinars")}
      </p>
      <div className="w-full relative mb-12">
        <CarouselProvider
          naturalSlideWidth={5}
          naturalSlideHeight={10}
          totalSlides={webinars?.length || 0}
          visibleSlides={slidesNumber}
          isIntrinsicHeight
        >
          {showButtons && (
            <ButtonBack>
              <button className="absolute top-[110px] left-0 left-[-5px] z-10 bg-white border border-black rounded-lg flex justify-center items-center">
                <LeftArrow className="h-6 w-6 p-2" />
              </button>
            </ButtonBack>
          )}
          <Slider>
            {webinars?.map((p: any, idx: number) => (
              <Slide key={idx} index={idx}>
                <div className="px-2">
                <Story content={p} />
                </div>
              </Slide>
            ))}
          </Slider>
          {showButtons && (
            <ButtonNext>
              <button className="absolute top-[110px] right-[-5px] z-10 bg-white border border-black rounded-lg flex justify-center items-center">
                <RigthArrow className="h-6 w-6 p-2" />
              </button>
            </ButtonNext>
          )}
        </CarouselProvider>
      </div>
    </>
  );
}
